import 'focus-visible/dist/focus-visible'
import React, { useState, useContext, createContext, useEffect } from 'react'
import qs from 'qs'

import { TermsModal } from 'src/components/custom/TermsModal'
import { IS_BROWSER } from '../localConstants'
import {
  clientConfig,
  allowedHostnames,
  allowedClients,
} from '../data/whiteLabelData'

// For some reason, we need to set default states here for
// these specific variables or the build will fail
const defaultState = {
  seAlert: () => {},
}

const WhiteLabelContext = createContext(defaultState)
const useWhiteLabelContext = () => useContext(WhiteLabelContext)

const WhiteLabelProvider = ({
  children,
  location,
  needsToAcceptTerms = true,
}) => {
  function getIsLocalStorageSupported() {
    try {
      const key = '____test____key'
      localStorage.setItem(key, key)
      localStorage.removeItem(key)
      return true
    } catch (e) {
      return false
    }
  }
  const isLocalStorageSupported = IS_BROWSER && getIsLocalStorageSupported()

  const { client: clientQuery, referrer: referrerQuery } = qs.parse(
    location?.search,
    {
      ignoreQueryPrefix: true,
    }
  )
  const [alert, setAlert] = useState()
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false)
  const [canAccess, setCanAccess] = useState(true)
  const { client: passedClient, location: passedLocation } =
    (IS_BROWSER && window.xprops) || {}

  // Client
  const client =
    passedClient ||
    clientQuery ||
    (isLocalStorageSupported && localStorage.getItem('client'))
  const isAllowedClient = client && allowedClients.includes(client)

  // Referrer
  const referrer =
    passedLocation?.hostname ||
    referrerQuery ||
    (isLocalStorageSupported && localStorage.getItem('referrer'))
  const isAllowedReferrer = referrer && allowedHostnames.test(referrer)

  // Sync app state with local storage
  useEffect(() => {
    if (isLocalStorageSupported) {
      if (localStorage.getItem('didAcceptTerms') !== 'true') {
        setIsTermsModalOpen(true)
        setHasAcceptedTerms(false)
      }
      if (localStorage.getItem('didAcceptTerms') === 'true') {
        setHasAcceptedTerms(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Set theme styles via CSS Custom Properties
  useEffect(() => {
    const root = document.documentElement.style
    if (isAllowedClient && clientConfig[client]) {
      Object.entries(clientConfig[client].colors).forEach(([key, value]) => {
        root.setProperty(`--${key}`, value)
      })
      if (isLocalStorageSupported) localStorage.setItem('client', client)
    }
    if (isAllowedReferrer && isLocalStorageSupported) {
      localStorage.setItem('referrer', referrer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Write to local storage when user accepts terms
  useEffect(() => {
    if (isLocalStorageSupported && hasAcceptedTerms) {
      localStorage.setItem('didAcceptTerms', 'true')
    }
  }, [hasAcceptedTerms, isLocalStorageSupported])

  useEffect(() => {
    setCanAccess(isAllowedClient && isAllowedReferrer)
  }, [isAllowedClient, isAllowedReferrer])

  return (
    <WhiteLabelContext.Provider
      value={{
        alert,
        setAlert,
        hasAcceptedTerms,
        setHasAcceptedTerms,
        isTermsModalOpen,
        setIsTermsModalOpen,
        client,
        referrer,
        canAccess,
        passedLocation,
        clientConfig,
      }}
    >
      {children}
      {canAccess && needsToAcceptTerms && (
        <TermsModal
          hasAcceptedTerms={hasAcceptedTerms}
          setHasAcceptedTerms={setHasAcceptedTerms}
          isTermsModalOpen={isTermsModalOpen}
          setIsTermsModalOpen={setIsTermsModalOpen}
        />
      )}
    </WhiteLabelContext.Provider>
  )
}

export { WhiteLabelProvider, WhiteLabelContext, useWhiteLabelContext }
